'use client';

import { type BoxProps } from '@ironhack/design-system2/components/box';
import { Flex } from '@ironhack/design-system2/components/flex';
import { Tab, TabList, Tabs } from '@ironhack/design-system2/components/tab';
import { Text } from '@ironhack/design-system2/components/text';
import {
  anyPass,
  isEmpty,
  isNil,
  join,
  propEq,
  reject,
  startsWith,
} from 'ramda';
import {
  type ReactElement,
  type RefObject,
  createRef,
  useCallback,
  useMemo,
  useRef,
  useState,
} from 'react';

import { Button } from '@/app/components/client/button';
import { ModuleWrapper } from '@/app/components/client/module-wrapper';
import { CancelWrap } from '@/app/components/server/cancel-wrap';
import { type DatoButtonBlock } from '@/lib/datocms/types';
import { CLICK_MARGIN, NAV_HEIGHT } from './measurements';
import { type RegisteredTarget } from './navigation-context';
import { NavigationContextProvider } from './navigation-context/navigation-context-provider';
import { useIsStuck } from './use-is-stuck';
import { useScrollingIndexUpdate } from './use-scrolling-index-update';

export type SecondaryNav = {
  buttons?: DatoButtonBlock[];
  navTitle: string;
} | null;

type Props = {
  data: SecondaryNav;
  tabTitles: Array<string | undefined>;
} & BoxProps;

// NavigationTabs serves as a page's secondary navigation.
export const NavigationTabs = (props: Props): ReactElement | null => {
  const { children, data, tabTitles, ...boxProps } = props;

  const navRef = useRef<HTMLDivElement>(null);
  const [navIndex, setNavIndex] = useState<number>(0);
  // targetRefs are in MOUNT ORDER of the child components.
  const [targetRefs, setTargetRefs] = useState<RegisteredTarget[]>([]);

  const { isStuck, top } = useIsStuck(navRef);

  const navigationTitles: string[] = reject(
    anyPass([isNil, isEmpty, startsWith('*')]) as (
      title: string | undefined
    ) => title is undefined | '' | `*${string}`,
    tabTitles
  );

  // Memoize to not recreate extra refs for no reason.
  // tabRefs are in CORRECT order of the navigation tab titles.
  const tabRefs: Array<RefObject<HTMLButtonElement | null>> = useMemo(
    () => navigationTitles.map(() => createRef<HTMLButtonElement>()),
    [navigationTitles]
  );

  const onIndexUpdate = useCallback(
    (targetTitle: string, goNextIndex: boolean): void => {
      let index = targetRefs.findIndex(propEq('title', targetTitle));
      if (index === -1) return;

      if (goNextIndex && tabRefs[index + 1]) {
        index += 1;
      }

      setNavIndex(index);

      // Scroll horizontally on secondary nav for this tab
      tabRefs[index]?.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest', // scroll horizontally only
      });
    },
    [setNavIndex, tabRefs, targetRefs]
  );

  useScrollingIndexUpdate(targetRefs, onIndexUpdate);

  if (!data || navigationTitles.length === 0) return <>{children}</>;

  const { buttons = [], navTitle } = data;

  const scrollToTargetTitled = (title: string): void => {
    const target = targetRefs.find(propEq('title', title));
    if (target) {
      target.ref.current?.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <NavigationContextProvider setTargetRefs={setTargetRefs}>
      <ModuleWrapper
        innerProps={{ align: 'center' }}
        moduleFormat={{
          background: 'white',
          spacingBottom: '24',
          spacingTop: '24',
        }}
        outerProps={{
          as: 'nav',
          boxShadow: isStuck
            ? join(',', [
                '0 1px 3px 0 rgba(0 0 0 / 10%)',
                '0 1px 2px 0 rgba(0 0 0 / 6%)',
              ])
            : 'unset',
          height: `${NAV_HEIGHT}px`,
          position: 'sticky',
          top,
          zIndex: '100',
          ...boxProps,
        }}
        ref={navRef}
      >
        {/* Like cohort selector this could benefit from breaking container */}
        <CancelWrap flexGrow={1} withPadding>
          <Flex gap={3} justify="space-between" minWidth="full">
            <Flex align="center" gap={3}>
              {navTitle && <Text textStyle="l">{navTitle}</Text>}

              {/* Margin bottom to compensate for selected border tab bottom */}
              <Tabs index={navIndex} mb={-1} onChange={setNavIndex}>
                <TabList gap={[3, null, 5]}>
                  {navigationTitles.map((title, index) => (
                    <Tab
                      key={title}
                      onClick={() => scrollToTargetTitled(title)}
                      ref={tabRefs[index]}
                      scrollMargin={`0 ${CLICK_MARGIN}px`}
                    >
                      {title}
                    </Tab>
                  ))}
                </TabList>
              </Tabs>
            </Flex>

            {buttons.length > 0 && (
              <Flex align="center" gap={2}>
                {buttons.map(({ id, ...button }) => (
                  <Button key={id} {...button} />
                ))}
              </Flex>
            )}
          </Flex>
        </CancelWrap>
      </ModuleWrapper>

      {children}
    </NavigationContextProvider>
  );
};
